<script lang="ts">
    import { getPoliticianScore, getTextColorByParty, organizeProfileIntoKeyValuePairs } from '../../api/elections';
    import { directoryStore, getMessageDeliveryOptions, onProfileImageError, type Profile } from '../../api/officials';
    import CaretRightIcon from '../../icons/CaretRightIcon.svelte';
    import EmailIcon from '../../icons/EmailIcon.svelte';
    import ExternalPageIcon from '../../icons/ExternalPageIcon.svelte';
    import FacebookIcon from '../../icons/FacebookIcon.svelte';
    import LoadingIcon from '../../icons/LoadingIcon.svelte';
    import LocationIcon from '../../icons/LocationIcon.svelte';
    import PhoneIcon from '../../icons/PhoneIcon.svelte';
    import StatsIcon from '../../icons/StatsIcon.svelte';
    import TwitterIcon from '../../icons/TwitterIcon.svelte';
    import YouTubeIcon from '../../icons/YouTubeIcon.svelte';
    import Accordion from '../../lib/Accordion.svelte';
    import Button from '../../lib/Button.svelte';
    import MessageIcon from '../../icons/MessageIcon.svelte';
    import * as t from '../../translations/Directory.ts';
    import {push, pop} from 'svelte-spa-router'

    const familyKey: string = 'Family';
    const birthplaceKey: string = 'Birthplace';
    const emailAddressKey: string = 'Email Address';
    const homeKey: string = 'Home';
    const webSiteKey: string = 'Web Site';
    const religionKey: string = 'Religion';
    const dobKey: string = 'Date of Birth';

    const capitolAddressKey: string = 'Capitol Address';
    const capitolPhoneKey: string = 'Capitol Phone';
    const districtAddressKey: string = 'District Address';
    const districtPhoneKey: string = 'District Phone';
    const facebookKey: string = 'Facebook';
    const xKey: string = 'X';
    const youTubeKey: string = 'YouTube';
    const occupationKey: string = 'Occupation';
    const committeesKey: string = 'Committees';
    const assumedOfficeKey: string = 'Assumed Office';
    const staffCapitolOfficeKey: string = 'Capitol Office';
    const staffDistrictOffice1Key: string = 'District Office 1';
    const staffDistrictOffice2Key: string = 'District Office 2';
    const iconColor = 'var(--vv-black)';

    let canSendMessage = false;
    let keyValueProfile: { [key: string]: any } | undefined;
    let firstPoliticalAccordionIsAlreadyExpanded: boolean = false;
    export let params;
    export let getPreviousRoute;
    /** Hosts outside the directory (e.g. Bills) pass the profile, their own back action, and where "Send Message" goes. */
    export let profile: Profile | undefined = undefined;
    export let showBackButton = true;
    export let onSendMessage: (() => void) | undefined = undefined;

    function haskeyValueProfileInfo(keys: string[]): boolean {
        let hasAny = false;
        for (const key of keys) {
            if (keyValueProfile && keyValueProfile[key]) {
                hasAny = true;
                break;
            }
        }
        return hasAny;
    }

    function shouldAccordionBeExpanded(): boolean {
        if (firstPoliticalAccordionIsAlreadyExpanded) return !firstPoliticalAccordionIsAlreadyExpanded;

        firstPoliticalAccordionIsAlreadyExpanded = true;
        return firstPoliticalAccordionIsAlreadyExpanded;
    }

    function loadKeyValueProfile(profile: Profile) {
        keyValueProfile = organizeProfileIntoKeyValuePairs(profile);
        getPoliticianScore(+profile.id)
            .then((politicianScore) => {
                if (keyValueProfile && +keyValueProfile[`id`] === +profile.id)
                    keyValueProfile!['politicianScore'] = politicianScore;
            })
            .catch((error) => {
                console.log(error);
            });
    }

    async function load() {
        const resolvedProfile = profile ?? $directoryStore.categories
            .map(category => (category ? category : []).profiles.map(profiles => profiles))
            .flat()
            .find(p => p.id == params.id);
        
        loadKeyValueProfile(resolvedProfile);
        const messageDeliveryOptions = await getMessageDeliveryOptions('E', params.id);
        canSendMessage = messageDeliveryOptions.filter((messageDeliveryOption) => messageDeliveryOption.deliveryMethod === 'communicatingwithcongressapi').length === 0;
    }

    load();
</script>

{#if keyValueProfile}
    <div class="flex flex-col col-span-6 grid-cols-6">
        <div class="flex flex-col @xl:grid @xl:grid-cols-6 gap-4">
            <div class="flex flex-col @xl:col-span-3 gap-2">
                <div class="flex flex-row gap-4">
                    {#if showBackButton}
                        <div class="grow-0">
                            <Button class="text-xl" type="navigation" on:click={() => !getPreviousRoute || !getPreviousRoute() ? push('/Officials') : pop()}>
                                <div class="p-3">
                                    <CaretRightIcon
                                            style="transform: scaleX(-1);"
                                            color="var(--vv-action-center-button-color)"
                                    />
                                </div>
                            </Button>
                        </div>
                    {/if}
                    <div>
                        <img
                                on:error={onProfileImageError}
                                class="h-20 object-cover rounded-xl"
                                src={keyValueProfile?.photoUrl}
                                alt={keyValueProfile?.title + keyValueProfile?.displayName}
                        />
                    </div>
                    <div class="flex flex-row gap-2">
                        <div>
                            <h2>
								<span class="text-lg">
									<span class="text-nowrap">{keyValueProfile?.title} </span>
									<span class="text-nowrap">{keyValueProfile?.displayName}</span>
								</span>
                            </h2>
                            <h3 class="text-base">
								<span class={getTextColorByParty(keyValueProfile?.party)}
                                >{keyValueProfile?.party}</span
                                >
                                {#if keyValueProfile?.state}
                                    <span> - {keyValueProfile?.state}</span>
                                {/if}
                            </h3>
                            {#if keyValueProfile[assumedOfficeKey]}
                                <h4 class="text-sm">
                                    {t.Assumed_Office_Label()}
                                    {keyValueProfile[assumedOfficeKey]}
                                </h4>
                            {/if}
                        </div>
                        <div></div>
                    </div>
                </div>
                <div>
                    <div class="flex flex-row gap-2 my-2 text-sm">
                        {#if !keyValueProfile['politicianScore']}
							<span title={t.Loading_Scorecard_Tooltip()}>
								<LoadingIcon class="text-vv-grey-mid inline"></LoadingIcon>
							</span>
                        {:else if keyValueProfile['politicianScore'] && keyValueProfile['politicianScore'].score != null}
                            <div>
                                <a
                                        class="flex flex-row gap-4 text-sm"
                                        href="./Scorecard#/?page=scorecard-{keyValueProfile['politicianScore']
										.politicianId}"
                                        target="_blank"
                                >
                                    <StatsIcon color={iconColor}></StatsIcon>
                                    {keyValueProfile?.politicianScore.score}% - {t.View_Scorecard_Label()}
                                </a>
                            </div>
                        {/if}
                    </div>
                    {#if keyValueProfile[emailAddressKey] && keyValueProfile[emailAddressKey][0] !== null}
                        <div class="my-2">
                            <a
                                    href="mailto:{keyValueProfile[emailAddressKey][0]}"
                                    class="flex flex-row gap-4 text-sm"
                            >
                                <EmailIcon color={iconColor} />
                                {keyValueProfile[emailAddressKey][0]}
                            </a>
                        </div>
                    {/if}
                    <div class="flex flex-row gap-2 my-2">
                        {#if keyValueProfile[webSiteKey] && keyValueProfile[webSiteKey][0] !== null}
                            <div>
                                <a
                                        class="flex flex-row gap-4 text-sm"
                                        href={keyValueProfile[webSiteKey][0]}
                                        target="_blank"
                                >
                                    <ExternalPageIcon color={iconColor} />
                                    {keyValueProfile[webSiteKey][0]}
                                </a>
                            </div>
                        {/if}
                    </div>
                    {#if haskeyValueProfileInfo([facebookKey, xKey, youTubeKey])}
                        <div class="flex flex-row gap-4 my-1.5">
                            {#if keyValueProfile[facebookKey]}
                                <div>
                                    <a target="_blank" href={keyValueProfile[facebookKey]}
                                    ><FacebookIcon color={iconColor} /></a
                                    >
                                </div>
                            {/if}
                            {#if keyValueProfile[xKey]}
                                <div>
                                    <a target="_blank" href={keyValueProfile[xKey]}
                                    ><TwitterIcon color={iconColor} /></a
                                    >
                                </div>
                            {/if}
                            {#if keyValueProfile[youTubeKey]}
                                <div>
                                    <a target="_blank" href={keyValueProfile[youTubeKey]}
                                    ><YouTubeIcon color={iconColor} /></a
                                    >
                                </div>
                            {/if}
                        </div>
                    {/if}

                    <div class="@xl:flex @xl:flex-row gap-4 my-4">
                        {#if canSendMessage || (!keyValueProfile?.title
                            .toLowerCase()
                            .includes('senator') && !keyValueProfile?.title
                            .toLowerCase()
                            .includes('representative'))}
                            <div>
                                <Button
                                        type="action-center"
                                        class="my-2 w-full"
                                        on:click={() => onSendMessage ? onSendMessage() : push('/Officials/' + params.id + '/Message')}
                                >
                                    <div class="flex flex-row gap-2 px-4">
                                        <div class="grow">{t.Send_Message_Button_Text()}</div>
                                        <div><MessageIcon /></div>
                                    </div>
                                </Button>
                            </div>
                        {/if}
                    </div>
                </div>
            </div>
            <div class="col-span-2">
                {#if haskeyValueProfileInfo([capitolAddressKey, capitolPhoneKey])}
                    <div class="">
                        <h2>{t.Capitol_Label()}</h2>
                        <div class="col-span-3 text-sm">
                            {#if keyValueProfile[capitolAddressKey]}
                                {#each keyValueProfile[capitolAddressKey] as value (value)}
                                    <div class="p-2 flex flex-row gap-2">
                                        {#if value !== null}
                                            <div>
                                                <LocationIcon color={iconColor} />
                                            </div>
                                            <div class="my-auto">
                                                {value}
                                            </div>
                                        {/if}
                                    </div>
                                {/each}
                            {/if}
                            {#if keyValueProfile[capitolPhoneKey]}
                                {#each keyValueProfile[capitolPhoneKey] as value (value)}
                                    <div class="p-2 flex flex-row gap-2">
                                        {#if value !== null}
                                            <div>
                                                <PhoneIcon color={iconColor} />
                                            </div>
                                            <div class="my-auto">
                                                {value}
                                            </div>
                                        {/if}
                                    </div>
                                {/each}
                            {/if}
                        </div>
                    </div>
                {/if}
                {#if haskeyValueProfileInfo([districtAddressKey, districtPhoneKey])}
                    <div class="">
                        <h2>{t.District_Label()}</h2>
                        <div class="col-span-3 text-sm">
                            {#if keyValueProfile[districtAddressKey]}
                                {#each keyValueProfile[districtAddressKey] as value (value)}
                                    <div class="p-2 flex flex-row gap-2">
                                        {#if value !== null}
                                            <div>
                                                <LocationIcon color={iconColor} />
                                            </div>
                                            <div class="my-auto">
                                                {value}
                                            </div>
                                        {/if}
                                    </div>
                                {/each}
                            {/if}
                            {#if keyValueProfile[districtPhoneKey]}
                                {#each keyValueProfile[districtPhoneKey] as value (value)}
                                    <div class="p-2 flex flex-row gap-2">
                                        {#if value !== null}
                                            <div>
                                                <PhoneIcon color={iconColor} />
                                            </div>
                                            <div class="my-auto">
                                                {value}
                                            </div>
                                        {/if}
                                    </div>
                                {/each}
                            {/if}
                        </div>
                    </div>
                {/if}
            </div>
        </div>
        {#if haskeyValueProfileInfo( [religionKey, familyKey, dobKey, birthplaceKey, homeKey, occupationKey, committeesKey, staffCapitolOfficeKey] )}
            <div class="my-4 border-b border-[var(--vv-grey-light)]"></div>
            <div class="@xl:grid @xl:grid-cols-4 gap-12">
                {#if haskeyValueProfileInfo([religionKey, familyKey, dobKey, birthplaceKey, homeKey])}
                    <div class="col-span-2">
                        <div
                                class="border-l-2 border-solid border-[var(--vv-action-center-button-color)] px-4 flex flex-col"
                        >
                            <div class="text-lg">{t.Background_Label()}</div>
                        </div>
                        {#if keyValueProfile[religionKey]}
                            <div class="flex flex-col @xl:grid @xl:grid-cols-4 my-4">
                                <h2>{t.Religion_Label()}</h2>
                                <ul class="col-span-3">
                                    {#each keyValueProfile[religionKey] as value (value)}
                                        <li>
                                            {#if value !== null}
                                                {value}
                                            {/if}
                                        </li>
                                    {/each}
                                </ul>
                            </div>
                        {/if}
                        {#if keyValueProfile[familyKey]}
                            <div class="flex flex-col @xl:grid @xl:grid-cols-4 my-4">
                                <h2>{t.Family_Label()}</h2>
                                <ul class="col-span-3">
                                    {#each keyValueProfile[familyKey] as value (value)}
                                        <li>
                                            {#if value !== null}
                                                {value}
                                            {/if}
                                        </li>
                                    {/each}
                                </ul>
                            </div>
                        {/if}
                        {#if keyValueProfile[dobKey]}
                            <div class="flex flex-col @xl:grid @xl:grid-cols-4 my-4">
                                <h2>{t.DateofBirth_Label()}</h2>
                                <ul class="col-span-3">
                                    {#each keyValueProfile[dobKey] as value (value)}
                                        <li>
                                            {#if value !== null}
                                                {value}
                                            {/if}
                                        </li>
                                    {/each}
                                </ul>
                            </div>
                        {/if}
                        {#if keyValueProfile[birthplaceKey]}
                            <div class="flex flex-col @xl:grid @xl:grid-cols-4 my-4">
                                <h2>{t.Birthplace_Label()}</h2>
                                <ul class="col-span-3">
                                    {#each keyValueProfile[birthplaceKey] as value (value)}
                                        <li>
                                            {#if value !== null}
                                                {value}
                                            {/if}
                                        </li>
                                    {/each}
                                </ul>
                            </div>
                        {/if}
                        {#if keyValueProfile[homeKey]}
                            <div class="flex flex-col @xl:grid @xl:grid-cols-4 my-4">
                                <h2>{t.Home_Label()}</h2>
                                <ul class="col-span-3">
                                    {#each keyValueProfile[homeKey] as value (value)}
                                        <li>
                                            {#if value !== null}
                                                {value}
                                            {/if}
                                        </li>
                                    {/each}
                                </ul>
                            </div>
                        {/if}
                    </div>
                {/if}
                {#if haskeyValueProfileInfo( [occupationKey, committeesKey, staffCapitolOfficeKey, staffDistrictOffice1Key, staffDistrictOffice2Key] )}
                    <div class="col-span-2">
                        <div
                                class="border-l-2 border-solid border-[var(--vv-action-center-button-color)] px-4 flex flex-col"
                        >
                            <div class="text-lg">{t.Political_Label()}</div>
                        </div>
                        {#if keyValueProfile[occupationKey]}
                            <Accordion
                                    class="col-span-3"
                                    shouldBeExpanded={shouldAccordionBeExpanded()}
                                    headerText={t.Occupation_Label()}
                            >
                                <ul class="">
                                    {#each keyValueProfile[occupationKey] as value (value)}
                                        <li>
                                            {#if value !== null}
                                                <pre class="whitespace-pre-wrap">{value}</pre>
                                            {/if}
                                        </li>
                                    {/each}
                                </ul>
                            </Accordion>
                        {/if}
                        {#if keyValueProfile[committeesKey]}
                            <Accordion
                                    shouldBeExpanded={shouldAccordionBeExpanded()}
                                    headerText={t.Committees_Label()}
                            >
                                <ul class="col-span-3">
                                    {#each keyValueProfile[committeesKey] as value (value)}
                                        <li>
                                            {#if value !== null}
                                                <pre class="whitespace-pre-wrap">{value}</pre>
                                            {/if}
                                        </li>
                                    {/each}
                                </ul>
                            </Accordion>
                        {/if}
                        {#if haskeyValueProfileInfo( [staffCapitolOfficeKey, staffDistrictOffice1Key, staffDistrictOffice2Key] )}
                            <Accordion
                                    shouldBeExpanded={shouldAccordionBeExpanded()}
                                    headerText={t.Staff_Label()}
                            >
                                {#if keyValueProfile[staffCapitolOfficeKey]}
                                    <h4>{t.Staff_Capitol_Office_Label()}:</h4>
                                    <ul class="pl-8">
                                        {#each keyValueProfile[staffCapitolOfficeKey] as value (value)}
                                            <li>
                                                {#if value !== null}
                                                    <pre class="whitespace-pre-wrap">{value}</pre>
                                                {/if}
                                            </li>
                                        {/each}
                                    </ul>
                                {/if}
                                {#if keyValueProfile[staffDistrictOffice1Key]}
                                    <h4>{t.Staff_District_Office_1_Label()}:</h4>
                                    <ul class="pl-8">
                                        {#each keyValueProfile[staffDistrictOffice1Key] as value (value)}
                                            <li>
                                                {#if value !== null}
                                                    <pre class="whitespace-pre-wrap">{value}</pre>
                                                {/if}
                                            </li>
                                        {/each}
                                    </ul>
                                {/if}
                                {#if keyValueProfile[staffDistrictOffice2Key]}
                                    <h4>{t.Staff_District_Office_2_Label()}:</h4>
                                    <ul class="pl-8">
                                        {#each keyValueProfile[staffDistrictOffice2Key] as value (value)}
                                            <li>
                                                {#if value !== null}
                                                    <pre class="whitespace-pre-wrap">{value}</pre>
                                                {/if}
                                            </li>
                                        {/each}
                                    </ul>
                                {/if}
                            </Accordion>
                        {/if}
                    </div>
                {/if}
            </div>
        {/if}
    </div>
{/if}
